
import { Link } from "react-router-dom";
import { type FC, type PropsWithChildren } from "react";

type FooterLink = {
  href?: string;
  to?: string;
  label: string;
};

type FooterLinkProps = PropsWithChildren<{
  to?: string;
  href?: string;
  className?: string;
}>;

const FOOTER_LINKS: FooterLink[] = [
  { href: "mailto:contact@ieltswith.ai", label: "contact@ieltswith.ai" },
  { to: "/refunds-policy", label: "Refunds Policy" },
  { to: "/terms-and-conditions", label: "Terms & Conditions" },
  { to: "/privacy-policy", label: "Privacy Policy" }
] as const;

const FooterLink: FC<FooterLinkProps> = ({
  to,
  href,
  children,
  className = ""
}) => {
  const baseClassName = "text-sm text-stone-400 transition-colors hover:text-orange-500 dark:hover:text-orange-500";
  const combinedClassName = `${baseClassName} ${className}`.trim();

  if (href) {
    return (
      <a href={href} className={combinedClassName}>
        {children}
      </a>
    );
  }

  if (!to) {
    throw new Error('Either "to" or "href" prop must be provided to FooterLink');
  }

  return (
    <Link to={to} className={combinedClassName}>
      {children}
    </Link>
  );
};

const Footer = () => {
  return (
    <footer
      className="border-t border-stone-200 dark:border-stone-600 py-8 bg-white dark:bg-stone-800"
      aria-labelledby="footer-heading"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <h2 id="footer-heading" className="sr-only">
          Footer
        </h2>

        <div className="flex flex-col items-center justify-between gap-4 md:flex-row md:gap-6">
          <p className="text-sm text-stone-400">
            © {new Date().getFullYear()} IELTSwith.ai. All rights reserved.
          </p>

          <nav aria-label="Footer navigation" className="flex gap-6">
            {FOOTER_LINKS.map((link) => (
              <FooterLink key={link.label} {...link}>
                {link.label}
              </FooterLink>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
